import { runWithQoS, Feature, createOwsError } from '../../shared';
import type { TraceErrorObject } from 'owa-trace';
import { getConfig } from 'owa-service/lib/config';

const actionName = 'ExportContactList';
const makeRequest = (params: { FolderId?: string }) => {
    const config = getConfig();
    const baseActionUrl = (config.baseUrl || '/owa') + '/service.svc/s/' + actionName;
    const url = `${baseActionUrl}${
        params.FolderId ? `?parentFolderId=${encodeURIComponent(params.FolderId)}` : ''
    }`;
    return fetch(url, {
        credentials: 'same-origin',
    });
};

// ReadableStreamReader#read() could return the body over multiple chunks, signalling when done
// This method iterates over #read() promises, then returns all the chunks concatentated together
// This is necessary as Response#arrayBuffer (continuous) is not present in IE.
// https://msdn.microsoft.com/en-us/library/mt709010(v=vs.85).aspx
const readEntireBodyFromStreamReader = (reader: ReadableStreamReader<Uint8Array>) =>
    new Promise<Uint8Array>((resolve, reject) => {
        try {
            const chunks: Uint8Array[] = [];
            const recursivelyRead = (onComplete: (result: Uint8Array) => void) => {
                reader
                    .read()
                    .then(read => {
                        if (read.value) {
                            chunks.push(read.value);
                        }

                        if (read.done) {
                            // Combine chunks into a single Uint8Array
                            const resultLength = chunks
                                .map(chunk => chunk.length)
                                .reduce((r, e) => r + e, 0);
                            let currentIndex = 0;
                            const resultArray = new Uint8Array(resultLength);
                            chunks.forEach(chunk => {
                                resultArray.set(chunk, currentIndex);
                                currentIndex += chunk.length;
                            });

                            // Resolve promise with concatenated array
                            onComplete(resultArray);
                        } else {
                            // There is still more data to read from the body
                            recursivelyRead(resolve);
                        }
                    })
                    .catch(() => reject());
            };
            recursivelyRead(resolve);
        } catch {
            reject();
        }
    });

export const exportContactList = (folderId?: string): Promise<Uint8Array> =>
    runWithQoS({
        scenarioName: actionName,
        feature: Feature.ExportContacts,
        operation: makeRequest({ FolderId: folderId }).then(response => {
            const success = response.status === 200;
            try {
                if (success && response.body) {
                    return readEntireBodyFromStreamReader(response.body.getReader());
                }

                // In some browsers (namely Firefox), .body is absent from the response and we extract it through the ArrayBuffer function
                if (success && response.arrayBuffer) {
                    return response.arrayBuffer().then(buffer => new Uint8Array(buffer));
                }
            } catch {
                throw createOwsError({
                    name: 'ExportContactListTransformError',
                    message: 'Error transforming response',
                    isTransformError: true,
                    response,
                });
            }

            const error: TraceErrorObject = new Error(success ? 'Missing response.body' : '');
            error.fetchErrorType = 'ServerFailure';
            error.response = response;
            error.retriable = false;
            throw createOwsError({
                name: 'ExportContactListResponseError',
                message: 'Missing response.body',
                serviceFetchError: error,
                isTransformError: false,
                response,
            });
        }),
    });
